import './AssetCollectionField.scss';
import AssetEditModal from './AssetEditModal';
import AssetSelectModal from './AssetSelectModal';
import ImageAsset from './ImageAsset';
import React from 'react';
import ReactDOM from 'react-dom';
import asset from '../../model/asset';
import {
  SortableContainer as sortableContainer,
  SortableElement as sortableElement,
  arrayMove
} from 'react-sortable-hoc';
import {useTagsHoC} from '../../providers/TagsProvider';
import tagModel from '../../model/tag';

class AssetListItem extends React.Component {
  render() {
    return (
      <li className="contentful-asset-list-item">
        <ImageAsset
          asset={this.props.asset}
          disabled={this.props.disabled}
          onRemove={this.props.onRemove}
          onEdit={this.props.onEdit}
          aspect={1}
          compact={true}
        />
      </li>
    );
  }
}

AssetListItem.defaultProps = {
  asset: null,
  index: null
};

const SortableAssetListItem = sortableElement(AssetListItem);

class AssetList extends React.Component {
  constructor(props) {
    super(props);

    this.activeItem = null;
  }

  render() {
    return (
      <div className="contentful-asset-collection-wrapper">
        <ul className="list-unstyled">
          {this.props.items.map(this.renderItem.bind(this))}
        </ul>
      </div>
    );
  }

  renderItem(asset, index) {
    // Make sure we store a reference back to the item at the active index:
    const active = index === this.props.activeIndex;
    const props = {
      key: `item-${index}-${asset.id}`,
      index: index,
      asset: asset,
      onRemove: () => {
        this.props.onRemove(index);
      },
      onEdit: this.props.onEdit,
      disabled: this.props.disabled
    };

    if (active) {
      props.ref = (element) => {
        this.activeItem = element;
      };
    }

    return (
      <SortableAssetListItem {...props} />
    );
  }

  componentDidUpdate(prevProps) {
    // Check whether the active index changed and we need to scroll a new item
    // into view:
    if (this.props.activeIndex !== prevProps.activeIndex &&
        this.activeItem
    ) {
      // Look up the actual DOM node from the SortableAssetListItem
      // component instance.
      const element = ReactDOM.findDOMNode(this.activeItem); // eslint-disable-line react/no-find-dom-node

      element.scrollIntoView({
        block: 'start',
        behavior: 'smooth'
      });
    }
  }
}

AssetList.defaultProps = {
  items: null,
  onRemove: () => {}
};

const SortableAssetList = sortableContainer(AssetList);

class AssetCollectionField extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      uploading: false,
      selecting: false,
      activeIndex: 0,
      error: ''
    };

    this.handleSortEnd = this.handleSortEnd.bind(this);
    this.handleToggleSelector = this.handleToggleSelector.bind(this);
    this.handleRemove = this.handleRemove.bind(this);
    this.handleAssetChange = this.handleAssetChange.bind(this);
    this.handleAssetEdit = this.handleAssetEdit.bind(this);
    this.handleSelection = this.handleSelection.bind(this);
    this.handleUpload = this.handleUpload.bind(this);

    this.imageRef = React.createRef();
  }

  handleSortEnd({oldIndex, newIndex}) {
    if (!this.props.disabled) {
      const newValue = arrayMove(this.props.value, oldIndex, newIndex);
      this.props.onChange(this.props.name, newValue);
    }
  }

  handleToggleSelector(e) {
    e.preventDefault();

    if (!this.props.disabled) {
      this.setState({
        selecting: !this.state.selecting
      });
    }
  }

  handleRemove(removedIndex) {
    if (!this.props.disabled) {
      const newValue = [].concat(this.props.value);
      newValue.splice(removedIndex, 1);
      this.props.onChange(this.props.name, newValue);
    }
  }

  handleAssetChange(editedAsset) {
    if (!this.props.disabled) {
      const newValue = this.props.value.map((asset) => {
        if (asset.id === editedAsset.id) {
          return editedAsset;
        }

        return asset;
      });

      this.props.onChange(this.props.name, newValue);
      this.setState({
        editingAsset: null
      });
    }
  }

  handleAssetEdit(editedAsset) {
    if (!this.props.disabled) {
      this.setState({
        editingAsset: editedAsset
      });
    }
  }

  handleSelection(selection) {
    if (!this.props.disabled) {
      const newValue = this.props.value.concat([selection]);
      this.props.onChange(this.props.name, newValue);
      this.setState({
        selecting: false,
        activeIndex: newValue.length - 1
      });
    }
  }

  async handleUpload() {
    if (!this.imageRef.current.files.length) {
      return;
    }

    const image = this.imageRef.current.files[0];

    if (!this.props.disabled) {
      this.setState({
        uploading: true,
        error: ''
      });

      let assetTagIDs = [];
      // If we are dealing with any predefined tags then we fetch existing tags, check which ones we already have and create the new ones.
      if (Array.isArray(this.props.predefinedTags) && this.props.predefinedTags.length > 0) {
        assetTagIDs = await tagModel.getOrCreateTagIdsFromTags(this.props.predefinedTags).catch(() => {
          this.setState({
            uploading: false,
            error: 'Feil ved opplasting av fil (Tag feilet). Ta kontakt!'
          });
          return null;
        });

        if (assetTagIDs === null) {
          return;
        }

        // Refresh the tags in the provider via the HOC.
        // Doing this async however if a race condition appears this can be awaited.
        this.props.tags.refreshTags();
      }

      asset.upload({
        filename: image.name,
        contentType: image.type,
        content: image
      }, assetTagIDs).then((asset) => {
        this.handleSelection(asset);
      }).then(() => {
        this.setState({
          uploading: false
        });
      });
    }
  }

  render() {
    return (
      <div className={`${this.props.disabled ? 'disabled ' : ''}contentful-asset-collection-field contentful-asset-collection-field-row`}>
        {Boolean(this.props.value.length) &&
          <SortableAssetList
            axis="x"
            lockAxis="x"
            items={this.props.value}
            onSortEnd={this.handleSortEnd}
            onRemove={this.handleRemove}
            onEdit={this.handleAssetEdit}
            helperClass="contentful-asset-list-item-sortable-helper"
            activeIndex={this.state.activeIndex}
            pressDelay={400}
            disabled={this.props.disabled}
          />
        }
        {!this.props.value.length &&
          <div className="contentful-asset-collection-empty well h4">
            Bildegalleriet er tomt.
          </div>
        }
        {this.state.error && <p>
          <span className="text-danger">{this.state.error}</span>
        </p>}
        <p className="text-left">
          <a
            className={`${(this.state.selecting ? 'active ' : '') + (this.props.disabled ? 'disabled ' : '')}btn btn-default`}
            onClick={this.handleToggleSelector}
          >
            <i className="fa fa-plus" aria-hidden="true"></i> Legg til
          </a>
          &nbsp;
          {this.state.uploading &&
            <span><i className="fa fa-spinner fa-pulse fa-fw"></i> Laster opp...</span>
          }
          {!this.state.uploading &&
            <label>
              <input className="hidden" type="file" ref={this.imageRef} onChange={this.handleUpload} accept="image/*" />
              <span className={`${this.props.disabled ? 'disabled ' : ''}btn btn-default`}><i className="fa fa-cloud-upload" aria-hidden="true"></i> Last opp</span>
            </label>
          }
        </p>

        <AssetEditModal
          asset={this.state.editingAsset}
          show={Boolean(this.state.editingAsset)}
          onChange={this.handleAssetChange}
          onHide={() => this.handleAssetEdit(null)}
        />

        <AssetSelectModal
          show={this.state.selecting}
          onSelect={this.handleSelection}
          onHide={this.handleToggleSelector}
        />
      </div>
    );
  }
}

AssetCollectionField.defaultProps = {
  value: [],
  name: 'asset',
  disabled: false,
  onChange: () => {},
  predefinedTags: []
};

export default useTagsHoC(AssetCollectionField);
